import dayjs from "dayjs";
import * as z from "zod";

export type ProposalDeliveryStatusValue = "scheduled" | "sent" | "failed" | "canceled";

export const proposalStatusLabel = (status?: string) => {
  switch (status) {
    case "scheduled":
      return "予約中";
    case "sent":
      return "送信済み";
    case "failed":
      return "送信失敗";
    case "canceled":
      return "キャンセル済み";
    default:
      return "";
  }
};

export const formatProposalDateTime = (value?: string | Date | null) => {
  if (!value) return "";
  const date = dayjs(value);
  return date.isValid() ? date.format("YYYY/MM/DD HH:mm") : "";
};

export const formatProposalDate = (value?: string | Date | null) => {
  if (!value) return "";
  const date = dayjs(value);
  return date.isValid() ? date.format("YYYY/MM/DD") : "";
};

type ProposalDateValue = string | Date | null | undefined;

/**
 * 予約日時プルダウンの1項目。
 *
 * `rawProposalDate` は scheduled_dates API がそのまま返した文字列（iso8601(6)・マイクロ秒付き）。
 * 一括取り消し API は proposal_date の完全一致で消すため、**この文字列は絶対に加工しない**
 * （生成クライアントが Date に変換するとマイクロ秒が落ちて一致しなくなる）。
 * 表示や行の絞り込みに使うときだけ dayjs でパースする。
 */
export type ScheduledProposalDateOption = {
  rawProposalDate: string;
  count: number;
};

export const formatScheduledDateOptionLabel = (option: ScheduledProposalDateOption) =>
  `${formatProposalDateTime(option.rawProposalDate)}（${option.count}件）`;

type SelectProposalsForListOptions<T> = {
  statusFilter?: ProposalDeliveryStatusValue;
  // 集約の単位（ユーザー一覧なら user_id、物件一覧なら property_id）
  getGroupKey: (proposal: T) => number;
  getProposalDate: (proposal: T) => ProposalDateValue;
};

/**
 * 一覧に表示する提案レコードを選ぶ。
 *
 * 予約中（scheduled）タブは「1予約 = 1行」。同一ユーザー・同一物件に複数の送信予約が入るため、
 * 集約すると「1通しか送られない」と誤認させてしまう。それ以外のタブは従来どおり
 * ユーザー（物件）単位で最新の1件に畳む。
 */
export const selectProposalsForList = <T>(proposals: T[], options: SelectProposalsForListOptions<T>): T[] => {
  const { statusFilter, getGroupKey, getProposalDate } = options;

  if (statusFilter === "scheduled") return [...proposals];

  const latestByKey = new Map<number, T>();

  proposals.forEach((proposal) => {
    const groupKey = getGroupKey(proposal);
    const existing = latestByKey.get(groupKey);
    if (!existing) {
      latestByKey.set(groupKey, proposal);
      return;
    }

    const existingTime = dayjs(getProposalDate(existing) ?? undefined);
    const candidateTime = dayjs(getProposalDate(proposal) ?? undefined);

    if (candidateTime.isAfter(existingTime)) {
      latestByKey.set(groupKey, proposal);
    }
  });

  return Array.from(latestByKey.values());
};

export const toProposalDateRequest = (value?: string | null) => {
  if (!value) return undefined;
  const date = dayjs(value);
  return date.isValid() ? date.toISOString() : undefined;
};

export const datetimeLocalMin = () => dayjs().add(1, "minute").format("YYYY-MM-DDTHH:mm");

export const SCHEDULED_AT_PAST_ERROR = "送信予約日時は現在より後の日時を指定してください。";

/**
 * 送信予約日時が「検証した時点で」未来かを判定する（未入力＝即時送信は対象外なので true）。
 *
 * input[type=datetime-local] の min（datetimeLocalMin）はレンダー時刻で固定されるため、
 * 入力後に時間が経つと「入力時は未来・送信時は過去」の日時がそのまま通過する。
 * 過去日時はサーバー側で即時送信として扱われ、置き換え確認（resolveScheduledConflict）も
 * スキップされるため、予約のつもりの送信が確認なしで即時発火してしまう。
 */
export const isFutureScheduledAt = (value?: string | null) => {
  if (!value) return true;
  const date = dayjs(value);
  return date.isValid() && date.isAfter(dayjs());
};

// 送信予約日時つきフォーム（投資提案・査定提案の登録/編集）で共通に使うスキーマ。
// refine はバリデーション実行時に評価されるため、送信時点での過去日時を弾ける
export const scheduledAtSchema = z
  .string()
  .optional()
  .refine(isFutureScheduledAt, { message: SCHEDULED_AT_PAST_ERROR });
